import { useEffect } from 'react'
import Styled from 'styled-components'
import { useHookstate } from '@hookstate/core'
import { useHistory, useLocation } from 'react-router-dom'
import { Button, Dropdown, message } from 'antd'
import Icons from '../../../../../Main/Components/Icons'
import { useTranslation } from 'react-i18next'
import Assign from '../../../../../domain/flag-management/job-detail/tasks/assign.component'
import { ConfirmModal } from './confirm-modal.component'
import { InfoDrawer } from './info-drawer.component'
import { updateExternalDataUseCase } from '../../application/update-external-data.use-case'
import { odinNebulaExternalDataRepository } from '../odin-nebula-external-data.repository'
import { ExternalDataPayload, UpdateExternalDataResponse } from '../../domain/entities'
import { useGate } from 'statsig-react'
import { AdditionalInfoGlobalState } from './additional-info-global-state'

const TableData = Styled.table`
  width: 100%;
  margin-bottom: 1rem;
`
const ListOptions = Styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  padding: 0.5rem 1.2rem;
  & > li{
    cursor: pointer;
    & > svg{
      margin-right: 0.5rem
    }
  }
`
const TableRow = Styled.tr`
  font-size: 1.4rem;
  .title{
    text-transform: capitalize;
    font-weight: bold;
    color: black;
  }
`
const ExternalDataLink = Styled.div`
  display: flex;
  align-items: center;

  a {
    text-decoration: underline;
  }

  svg {
    margin-left: 0.5em;
  }
`

type TableRowData = {
  infoTitle: string
  infoValue: string | JSX.Element
  idTitle?: string
  idValue?: string
}
type AdditionalInfoProps = {
  externalData: any
  setSelectedJob: React.Dispatch<React.SetStateAction<any>>
  jobId: string
  jobUuid: string
  deliveryTask: any
  store: any
  jobsSearch: any
  timezone: string
}
type ConfigLongInfoDrawer = {
  title?: string
  isVisible: boolean
  size?: number
  content?: any
}

const AdditionalInfo = ({
  externalData,
  setSelectedJob,
  jobId,
  jobUuid,
  deliveryTask,
  store,
  jobsSearch,
  timezone
}: AdditionalInfoProps) => {
  const isUrl = (text: string): boolean => {
    try {
      new URL(text)
      return true
    } catch (_) {
      return false
    }
  }
  const isAdditionalInfoActive = useGate('is_additional_info_active').value
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const CHARACTER_LIMIT: number = 100
  const KEY_CHARACTER_LIMIT: number = 20
  const backofficeData = useHookstate(externalData.backoffice)
  const isVisible = useHookstate<boolean>(false)
  const configLongInfoDrawer = useHookstate<ConfigLongInfoDrawer>({ isVisible: false })
  const showAssign = useHookstate<boolean>(false)
  const showModal = useHookstate<boolean>(false)
  const additionalResource = useHookstate<any>({})
  const resourceRows = useHookstate<TableRowData[]>([])
  const { deliveryResourceAssigned } = useHookstate(AdditionalInfoGlobalState)
  const updateExternalDataUseCaseInteractor = updateExternalDataUseCase(odinNebulaExternalDataRepository)

  const updateExternalData = async (data: ExternalDataPayload) => {
    if (!data.jobUuid || !data.backofficeData) return
    const response: UpdateExternalDataResponse = await updateExternalDataUseCaseInteractor(data)
    return response
  }

  const getReducedText = (text: string, limitCharacters: number): string => {
    const numOfCharaters: number = text.length
    if (numOfCharaters < limitCharacters) {
      return `${text}`
    }
    const formattedText: string = `${text.slice(0, limitCharacters)} ${
      text[numOfCharaters - 1] === text[limitCharacters - 1] ? ' ' : '...'
    }`
    return formattedText
  }

  const configureAndDisplayLongTextInformationDrawer = (title: string, content: any): void => {
    configLongInfoDrawer.set({
      ...configLongInfoDrawer.get(),
      isVisible: true,
      title: `${title.charAt(0).toUpperCase()}${title.substring(1)}`,
      content: content
    })
  }
  const renderShortText = (key: string, keyValues: any): JSX.Element => (
    <>
      {getReducedText(keyValues[key], CHARACTER_LIMIT)}
      <Button
        type='dashed'
        size='small'
        onClick={() => configureAndDisplayLongTextInformationDrawer(key, keyValues[key])}
      >
        {t('jobs.job.info.showMore')}
      </Button>
    </>
  )

  const renderLink = (url: string): JSX.Element => (
    <ExternalDataLink>
      <a href={url} rel='noopener noreferrer' target='_blank'>
        {t('jobs.job.info.open-link')}
      </a>
      <Icons iconName={'Link'} />
    </ExternalDataLink>
  )

  const transformDataIntoOrderedElementPairs = (): void => {
    const resourcePairs: TableRowData[] = []
    const keyValues = backofficeData.get()
    for (const key in keyValues) {
      if (!key.startsWith('Additional')) {
        if (keyValues[key].length > CHARACTER_LIMIT || key.length > KEY_CHARACTER_LIMIT) {
          resourcePairs.push({
            infoTitle: getReducedText(key, KEY_CHARACTER_LIMIT),
            infoValue: isUrl(keyValues[key]) ? renderLink(keyValues[key]) : renderShortText(key, keyValues)
          })
        } else {
          resourcePairs.push({
            infoTitle: key,
            infoValue: isUrl(keyValues[key]) ? renderLink(keyValues[key]) : keyValues[key]
          })
        }
      }
      if (key.startsWith('Additional Resource ID')) {
        const idTitle = key
        const infoTitle = key.replace('Additional Resource ID', 'Additional Resource Name')
        const idValue = keyValues[idTitle]
        const infoValue = keyValues[infoTitle]
        resourcePairs.push({ idTitle, infoTitle, idValue, infoValue })
      }
    }

    resourcePairs.sort((a, b) => {
      const keyA = a.idTitle || a.infoTitle
      const keyB = b.idTitle || b.infoTitle

      if (keyA.includes('Additional') && keyB.includes('Additional')) {
        const numberA = parseInt(keyA.match(/\((\d+)\)/)[1])
        const numberB = parseInt(keyB.match(/\((\d+)\)/)[1])
        return numberA - numberB
      } else if (keyA.includes('Additional')) {
        return 1
      } else if (keyB.includes('Additional')) {
        return -1
      } else {
        return 0
      }
    })
    resourceRows.set(resourcePairs)
  }

  const generateTableRows = (pairs: TableRowData[]) =>
    pairs.map((pair, index) => {
      return pair.idTitle && pair.idValue ? (
        <TableRow key={index}>
          <td className='title'>{pair.infoTitle}:</td>
          <td>{pair.infoValue}</td>
          <td className='title'>{pair.idTitle}:</td>
          <td>{pair.idValue}</td>
        </TableRow>
      ) : (
        <TableRow key={index}>
          <td className='title'>{pair.infoTitle}:</td>
          <td colSpan={3}>{pair.infoValue}</td>
        </TableRow>
      )
    })

  const onOpenOrCloseReassignModal = () => {
    if (!location.pathname.includes(`re-assign`)) {
      history.push(`${location.pathname}/re-assign`)
    } else if (location.pathname.includes('re-assign') && showAssign.get()) {
      history.replace(location.pathname.split('/re-assign')[0])
    }
    showAssign.set(!showAssign.get())
  }

  const renderOverlay = () => (
    <ListOptions>
      <li onClick={() => onOpenOrCloseReassignModal()}>
        <Icons iconName='Truck' />
        {t('additional-info.additional-driver')}
      </li>
    </ListOptions>
  )

  const onSelectDriverForAdditionalInfo = (resource: any) => {
    const repeatedResource = resourceRows.get().find(({ idValue }) => idValue === resource.id)
    const newlyAssignedResourceRepeated =
      !deliveryTask.resources.resourceId &&
      deliveryResourceAssigned.get().id !== '' &&
      deliveryResourceAssigned.get().id === resource.id
    const repeatedMainResource = deliveryTask.resources.resourceId === resource.id

    if (!deliveryTask.resources.resourceId && deliveryResourceAssigned.get().id === '') {
      message.error(t(`additional-info.error-no-assignment`))
    } else if (repeatedResource || newlyAssignedResourceRepeated || repeatedMainResource) {
      message.error(t(`additional-info.error-repeated-driver`))
    } else {
      additionalResource.set(resource)
      showModal.set(true)
    }
    onOpenOrCloseReassignModal()
  }

  const addResourceToAdditionalInfo = async (resource: any) => {
    showModal.set(false)
    let lastNumber: number = 0
    resourceRows.get().forEach((pair) => {
      const key = pair.infoTitle
      if (key.includes('Additional Resource Name')) {
        lastNumber = parseInt(key.match(/\((\d+)\)/)[1])
      }
    })
    const newBackofficeData = { ...backofficeData.get() }
    newBackofficeData[`Additional Resource ID (${lastNumber + 1})`] = resource.id
    newBackofficeData[`Additional Resource Name (${lastNumber + 1})`] = resource.name
    const response = await updateExternalData({
      jobUuid: jobUuid,
      backofficeData: newBackofficeData
    })
    if (!response.updated) {
      message.error(t(`additional-info.error-adding-driver`))
      return
    }
    if (response.message === 'EXTERNAL_DATA_UPDATED_SUCCESSFULLY') {
      message.success(t(`additional-info.success-message`))
    }
    backofficeData.set(newBackofficeData)
  }

  useEffect(() => {
    transformDataIntoOrderedElementPairs()
  }, [backofficeData])

  return (
    <>
      <TableData>
        <tbody>{generateTableRows([...resourceRows.get({ noproxy: true })])}</tbody>
      </TableData>
      {isAdditionalInfoActive && (
        <Dropdown
          overlay={() => renderOverlay()}
          placement='bottomLeft'
          trigger={['click']}
          overlayClassName='additional-info-overlay'
        >
          <Button onClick={() => isVisible.set(!isVisible.get())} style={{ color: 'black' }}>
            {`+ ${t('additional-info.add-information')}`}
          </Button>
        </Dropdown>
      )}
      {showAssign.get() && (
        <Assign
          setSelectedJob={setSelectedJob}
          onClose={onOpenOrCloseReassignModal}
          jobId={jobId}
          task={deliveryTask || null}
          store={store}
          visible={showAssign.get()}
          jobsSearch={jobsSearch}
          tz={timezone}
          onSelectDriverForAdditionalInfo={onSelectDriverForAdditionalInfo}
        />
      )}
      <ConfirmModal showModal={showModal} action={() => addResourceToAdditionalInfo(additionalResource.get())} />
      {configLongInfoDrawer.get({ noproxy: true }).isVisible && (
        <InfoDrawer
          title={configLongInfoDrawer.get().title}
          visible={configLongInfoDrawer.get().isVisible}
          onClose={() =>
            configLongInfoDrawer.set({
              ...configLongInfoDrawer.get(),
              isVisible: !configLongInfoDrawer.get().isVisible
            })
          }
          style={{ whiteSpace: 'pre-line' }}
        >
          {configLongInfoDrawer.get().content}
        </InfoDrawer>
      )}
    </>
  )
}

export { AdditionalInfo }
